<template>
  <div>
    <div v-if="props.generatedCatalogData">
      <div v-if="props.errorPassed">
        <div v-if="props.errorPassed === 'NoArticlesFromSourceInCatalog'">
          {{ errors.NoArticlesInSourceCatalogue }}
        </div>

        <div v-else-if="errorPassed === 'CatalogNameExist'">
          <h3>{{ errors.CatalogueNameExist.title }}</h3>
          <p>{{ errors.CatalogueNameExist.message }}</p>
        </div>
      </div>

      <div v-else>
        <div class="mb-1">
          <div class="font-weight-bold">
            {{ $t("GenerateCatalogFromTextFile.CatalogFinishing.SelectedCatalogue") }}
          </div>
          <div>{{ props.generatedCatalogData.articlesInSourceCatalog }}</div>
        </div>

        <div class="mb-1">
          <div class="font-weight-bold">
            {{ $t("GenerateCatalogFromTextFile.CatalogFinishing.Source") }}
          </div>
          <div>{{ props.generatedCatalogData.articlesInSource }}</div>
        </div>

        <div class="mb-1">
          <div class="font-weight-bold">
            {{ $t("GenerateCatalogFromTextFile.CatalogFinishing.GeneratedCatalogue") }}
          </div>
          <div>{{ props.generatedCatalogData.articlesInCatalog }}</div>
        </div>

        <div class="my-3 d-flex" v-if="props.generatedCatalogData.nonExistingArticles.length > 0">
          <div>{{ $t("GenerateCatalogFromTextFile.CatalogFinishing.SaveText") }}</div>
          <b-btn class="ml-3" @click="downloadCsv">{{ $t("Download") }}</b-btn>
        </div>

        <div>
          <b-checkbox v-model="openGeneratedCatalogueModel">
            {{ $t("OpenGeneratedCatalogue") }}
          </b-checkbox>
        </div>
      </div>
    </div>

    <div v-else>
      <div class="mt-3 text-center">
        {{ $t("CatalogGenerationInProgress") }}
      </div>
      <div class="mb-3 mt-1">
        <SpinnerVue />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import SpinnerVue from "@common/components/Spinner.vue";
import { computed, reactive, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps<{
  generatedCatalogData: {
    articlesInSourceCatalog: number;
    articlesInSource: number;
    articlesInCatalog: number;
    nonExistingArticles: string[];
  } | null;
  newCatalogName?: string | null;
  errorPassed?: string | null;
  existingCatalogName?: string | null;
  openGeneratedCatalogue?: boolean;
}>();

const emit = defineEmits(["update:openGeneratedCatalogue"]);

//#region ViewModel
type ViewModel = {
  localOpenGeneratedCatalogue: boolean;
};

const vm = reactive<ViewModel>({
  localOpenGeneratedCatalogue: props.openGeneratedCatalogue ?? false
});
//#endregion

//#region Computed Errors
const errors = computed(() => ({
  NoArticlesInSourceCatalogue: `${t("ErrorMessage.NoArticlesInSourceCatalogue")}\n'${props.existingCatalogName || ""}'. ${t("ErrorMessage.NoCatalogueGenerated")}`,
  CatalogueNameExist: {
    title: t("ErrorMessage.CatalogueGenerationFailed"),
    message: t("ErrorMessage.CatalogueNameExist")
  }
}));
//#endregion

//#region Computed Properties
const openGeneratedCatalogueModel = computed({
  get: () => vm.localOpenGeneratedCatalogue,
  set: (val: boolean) => {
    vm.localOpenGeneratedCatalogue = val;
    emit("update:openGeneratedCatalogue", val);
  }
});
//#endregion

//#region Watch
watch(
  () => props.openGeneratedCatalogue,
  (val) => {
    if (val !== undefined) vm.localOpenGeneratedCatalogue = val;
  }
);
//#endregion

//#region Methods
const downloadCsv = () => {
  if (!props.generatedCatalogData) return;

  let csvContent = "data:text/csv;charset=utf-8,ArticleNo\n";
  props.generatedCatalogData.nonExistingArticles.forEach((value) => {
    csvContent += `${value}\n`;
  });

  const encodedUri = encodeURI(csvContent);
  const link = document.createElement("a");
  link.setAttribute("href", encodedUri);
  link.setAttribute("download", props.newCatalogName ? `${props.newCatalogName}.csv` : "catalogue.csv");
  document.body.appendChild(link);
  link.click();
};
//#endregion
</script>
